<template>
  <div class="content">
    <div class="content content--center" v-if="isImporting">
      <t-loader block :text="progressMessage || 'Идет импорт заявления...'"/>
    </div>

    <template v-else>
      <header class="join join--col">
        <h1>
          <span class="join join--col join--baseline">
            <span>
              <template v-if="store.application.sspvo_unique_code">
                Заявление №{{ store.application.sspvo_unique_code }}
              </template>
              {{ store.application.full_name }}
            </span>
            <span
              class="badge badge--pill badge--wrap"
              :class="store.application.has_enrolment_agreement ? 'badge--solid-success' : 'badge--solid-muted'"
            >
              {{ store.application.has_enrolment_agreement ? 'Подано согласие на зачисление' : 'Нет согласия на зачисление' }}
            </span>
          </span>

          <template v-if="isAdmin">
            {{ applicationKind }}
          </template>
        </h1>

        <div class="card card--banner" v-if="store.application.status === 'enrolled'">
          <img class="card__icon icon icon--primary" src="~images/info.svg" v-svg-inline/>
          <p>
            Поздравляем с поступлением!
            <br>
            Подробную информацию можно найти во вкладке <strong>Обзор</strong>
          </p>
        </div>

        <div class="card card--danger card--danger-dark" v-if="store.application.email_matched === false">
          <img class="card__icon icon icon--danger" src="~images/info.svg" v-svg-inline/>
          <p>
            <strong>ВНИМАНИЕ!</strong> <span v-html="APPLICATION_EMAIL_NOT_MATCHED_TEXT_APPLICATION"></span>
          </p>
        </div>

        <div v-if="isAdmin">
          <p><strong>ID пользователя:</strong> {{ store.application.abiturient_user_id }}</p>
          <p><strong>Email пользователя:</strong> {{ store.application.abiturient_user_email }}</p>
          <p><strong>ID абитуриента:</strong> {{ store.application.source_id }}</p>
        </div>
      </header>

      <div class="content content--aside">
        <aside class="join join--col">
          <nav class="menu">
            <router-link
              v-for="section in visibleSections"
              :key="section.key"
              :to="{ params: { ...route.params, section: section.key } }"
              class="menu__item"
            >
              {{ section.label }}
            </router-link>
          </nav>

          <template v-if="store.application?.id">
            <button
              class="btn btn--primary btn--lg"
              :disabled="isImporting"
              @click="startImport"
            >
              Обновить
            </button>
            <p>Последнее обновление:</p>
            <p>{{ store.application?.imported_at ? store.application.imported_at : 'не обновлялось' }}</p>
          </template>
        </aside>

        <!-- right part -->
        <div class="content">
          <div class="join join--mobile-col">
            <ul class="steps">
              <li class="steps__item" :class="stepsActiveClasses(status)" v-for="status in store.application.statuses">
                <div>{{ status.text }}</div>
              </li>
            </ul>
          </div>

          <component
            :is="activeSection.component"
            :readonly="readonly"
            @refetch="$emit('refetch')"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { APPLICATION_SECTIONS } from '../config/sections'
import { useApplicationStore } from '../model/store'
import { APPLICATION_EMAIL_NOT_MATCHED_TEXT_APPLICATION } from '@shared/config'
import { useApplicationImport } from '@shared/lib'
import { TLoader } from '@shared/ui'

const store = useApplicationStore()

defineProps({
  readonly: { type: Boolean, default: false },
})
const emit = defineEmits(['refetch'])

const route = useRoute()

const visibleSections = computed(() =>
  APPLICATION_SECTIONS.filter(s => !s.condition || s.condition(store))
)

const activeSection = computed(() => visibleSections.value.find(s => s.key === route.params.section) ?? visibleSections.value[0])
const formId = computed(() => route.params.formId)
const isAdmin = computed(() => Boolean(window.gon?.user?.is_admin))
const application = computed(() => store.application)

const applicationKind = computed(() => ({
  bachelor: 'Бакалавриат',
  magistrant: 'Магистрант',
  aspirant: 'Аспирант',
})[store.application?.education_level])

const statusPassedClass = (status) => {
  if (!store.application.passed_statuses || !store.application.passed_statuses.length) return false
  return store.application.passed_statuses.includes(status.value)
}

const stepsActiveClasses = (status) => {
  return {
    'steps__item--active': store.application.status === status.value,
    'steps__item--passed': statusPassedClass(status),
    'steps__item--danger': ['failed', 'withdrawn', 'not_passed', 'dismissed'].includes(status.value),
  }
}

const {
  progressMessage,
  isImporting,
  startImport,
} = useApplicationImport({
  application,
  formId,
  onFinished: () => emit('refetch'),
})
</script>
